// ---------------------------------------------------------------------------
// jsonRepair.js
// Tolerant JSON parsing for LLM output. Handles the common failure modes:
//   - markdown code fences or text around the JSON
//   - raw line breaks / tabs inside string values (invalid in JSON)
//   - trailing commas
//   - output cut off mid-way (max_tokens reached): the JSON is trimmed back
//     to the last complete value and the open strings/arrays/objects closed
// ---------------------------------------------------------------------------

// Replace raw control characters inside string literals with spaces.
const escapeControlCharsInStrings=(text)=> {
  let out='';
  let inString=false;
  let escaped=false;
  for (const ch of text) {
    if (inString) {
      if (escaped) { escaped=false; out+=ch; continue; }
      if (ch==='\\') { escaped=true; out+=ch; continue; }
      if (ch==='"') { inString=false; out+=ch; continue; }
      out+=ch < ' ' ? ' ' : ch;
    } else {
      if (ch==='"') inString=true;
      out+=ch;
    }
  }
  return out;
};

const removeTrailingCommas=(text)=> text.replace(/,\s*([}\]])/g,'$1');

// Work out what is needed to close a (possibly truncated) JSON prefix.
const closersFor=(text)=> {
  const stack=[];
  let inString=false;
  let escaped=false;
  for (const ch of text) {
    if (inString) {
      if (escaped) escaped=false;
      else if (ch==='\\') escaped=true;
      else if (ch==='"') inString=false;
    } else if (ch==='"') inString=true;
    else if (ch==='{') stack.push('}');
    else if (ch==='[') stack.push(']');
    else if (ch==='}' || ch===']') stack.pop();
  }
  return {inString,closers: stack.reverse().join('')};
};

const tryParse=(text)=> {
  try { return JSON.parse(text); } catch { return undefined; }
};

// Close a truncated JSON document. If it stopped between values, just add
// the missing closing brackets. If it stopped mid-string, that value is
// incomplete (a sentence cut in half), so cut back to the last complete
// value instead of keeping the fragment.
const repairTruncated=(text)=> {
  const {inString,closers}=closersFor(text);
  if (!inString) {
    const direct=tryParse(removeTrailingCommas(`${text}${closers}`));
    if (direct!==undefined) return direct;
  }

  let cut=text.length;
  for (let attempts=0;attempts < 400;attempts++) {
    cut=text.lastIndexOf(',',cut - 1);
    if (cut <=0) break;
    const prefix=text.slice(0,cut);
    const state=closersFor(prefix);
    if (state.inString) continue;            // comma was inside a string
    const parsed=tryParse(removeTrailingCommas(`${prefix}${state.closers}`));
    if (parsed!==undefined) return parsed;
  }
  return undefined;
};

/**
 * Parse JSON from LLM output.
 * @returns {{data: any, repaired: boolean}}
 * @throws if nothing usable can be recovered
 */
export const parseLlmJson=(raw)=> {
  const text=String(raw || '').trim();
  const direct=tryParse(text);
  if (direct!==undefined) return {data: direct,repaired: false};

  const unfenced=text.replace(/^```(?:json)?\s*/i,'').replace(/\s*```\s*$/i,'');
  const start=unfenced.indexOf('{');
  if (start===-1) throw new Error('LLM response contained no JSON object.');
  let body=unfenced.slice(start);
  const end=body.lastIndexOf('}');

  // Complete object with junk around it or minor syntax problems.
  if (end!==-1) {
    const candidate=removeTrailingCommas(escapeControlCharsInStrings(body.slice(0,end + 1)));
    const parsed=tryParse(candidate);
    if (parsed!==undefined) return {data: parsed,repaired: candidate!==body};
  }

  // Truncated object.
  body=escapeControlCharsInStrings(body);
  const repaired=repairTruncated(body);
  if (repaired!==undefined && typeof repaired==='object') return {data: repaired,repaired: true};

  throw new Error('LLM returned invalid JSON that could not be parsed or repaired.');
};
